import { Sparkles, ArrowDownRight } from "lucide-react";

/**
 * Generates a deterministic "Why this action was selected" explanation by
 * comparing the top action against the next 1-2 ranked alternatives.
 */
export default function WhyThisAction({ consensus }) {
  if (!consensus?.top_action) return null;
  const ranked = consensus.ranked_actions || [];
  const top = consensus.top_action;
  const alternatives = ranked.slice(1, 3);

  const reasons = buildReasons(top, alternatives, consensus);

  return (
    <div
      className="mt-3 border border-blue-500/30 bg-blue-500/[0.04] rounded-sm p-4"
      data-testid="why-this-action"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-blue-300" strokeWidth={1.75} />
        <span className="cre-overline text-blue-300">
          Why this action was selected
        </span>
      </div>
      <p className="mt-2 text-sm text-zinc-200 leading-relaxed">
        {reasons.headline}
      </p>
      {reasons.bullets.length ? (
        <ul className="mt-3 space-y-1.5 text-xs text-zinc-300">
          {reasons.bullets.map((b, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-blue-300 font-mono">›</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {alternatives.length ? (
        <div className="mt-4 border-t border-white/5 pt-3">
          <div className="cre-overline mb-1.5">Versus next-ranked options</div>
          <ul className="space-y-1.5">
            {alternatives.map((a, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-xs"
                data-testid={`why-alt-${i}`}
              >
                <ArrowDownRight
                  className="h-3.5 w-3.5 mt-0.5 text-zinc-500"
                  strokeWidth={1.75}
                />
                <div className="min-w-0">
                  <div className="text-zinc-200 truncate">{a.action}</div>
                  <div className="text-[11px] font-mono text-zinc-500">
                    U={Number(a.utility).toFixed(4)} · {a.proposer_agent} · {a.risk} risk
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function buildReasons(top, alternatives, consensus) {
  const bullets = [];
  const align = Number(consensus.alignment_score || 0);

  bullets.push(
    `Highest weighted utility (U=${Number(top.utility).toFixed(4)}) under the current consensus weights.`
  );

  if (top.risk === "low") {
    bullets.push(
      "Classified low-risk — reversible side-effects, low rollback complexity factored into U."
    );
  } else if (top.risk === "medium") {
    bullets.push(
      "Medium-risk action — weighted utility still leads after the rollback-complexity penalty."
    );
  } else {
    bullets.push(
      "Despite high-risk classification, utility leads because of severity (A) and business impact (B)."
    );
  }

  if (top.rollback) {
    bullets.push(`Has an explicit rollback path: "${top.rollback}".`);
  }

  if (alternatives.length) {
    const next = alternatives[0];
    const delta = Number(top.utility) - Number(next.utility);
    if (delta > 0) {
      bullets.push(
        `Outranks the next option (${next.proposer_agent}) by ΔU=${delta.toFixed(4)}.`
      );
    }
    const riskOrder = { low: 1, medium: 2, high: 3 };
    if ((riskOrder[top.risk] || 2) < (riskOrder[next.risk] || 2)) {
      bullets.push(
        `Strictly safer than the runner-up (${next.risk} risk) for comparable impact.`
      );
    }
  }

  if (align >= 0.7) {
    bullets.push(
      `Strong inter-agent agreement (${(align * 100).toFixed(0)}% alignment) reinforces this choice.`
    );
  } else if (align < 0.5) {
    bullets.push(
      `Inter-agent alignment is only ${(align * 100).toFixed(0)}% — proceed with cautious validation.`
    );
  }

  return {
    headline:
      `The consensus engine picked this action because it scores highest on the multi-criteria utility ` +
      `function while preserving rollback safety. Lower-ranked options either carried more risk for similar ` +
      `impact, or had weaker per-domain confidence.`,
    bullets,
  };
}
